<template>
    <div class="relative">
        <label class="relative block w-full">
            <span
                class="block text-slate-600 dark:text-slate-300 text-sm font-medium leading-tight mb-2"
            >
                {{ label }}
            </span>
            <input
                type="text"
                v-model="airportsQuery"
                class="block w-full h-10 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 py-1.5 text-slate-800 dark:text-slate-100 text-sm placeholder:text-slate-500 dark:text-slate-400 placeholder:text-sm transition-colors focus:ring-0 focus:ring-offset-0 focus:outline-none focus:border-slate-800 hover:border-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                :class="{ '!border-red-500': hasError }"
                :placeholder="placeholder"
                v-debounce="350"
                @input="searchAirports"
                @focus="updateFocus(true)"
                @blur="updateFocus(false)"
                @keydown.enter.prevent="enter()"
                @keydown.down="down"
                @keydown.up="up"
                autocomplete="off"
                v-if="!airportChosen"
            />
            <div
                class="relative w-full h-10 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 py-1.5 pl-4 pr-10 text-slate-800 dark:text-slate-100 text-sm placeholder:text-slate-500 dark:text-slate-400 placeholder:text-sm transition-colors focus:ring-0 focus:border-slate-800 hover:border-slate-800 flex items-center"
                :class="{ '!border-red-500': hasError }"
                v-if="airportChosen"
            >
                <span class="truncate">{{ airportChosenDisplay }}</span>
                <button
                    class="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:text-slate-300 transition-colors"
                    @click="reset()"
                    type="button"
                >
                    <svg
                        class="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        stroke-width="2"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M6 18L18 6M6 6l12 12"
                        ></path>
                    </svg>
                </button>
            </div>
            <span v-if="hasError" class="text-red-500 text-xs mt-1 block">{{ label }} is required</span>
        </label>
        <div
            class="absolute left-0 right-0 top-full z-10 pt-2"
            v-if="airportsQuery && airportsQuery.length > queryStartLength"
        >
            <ul
                id="airport-dropdown"
                class="block w-full max-h-40 min-h-16 overflow-y-auto p-2 pb-12 bg-white dark:bg-slate-800 rounded-lg shadow relative"
            >
                <li
                    class="w-full flex items-center justify-center"
                    v-if="isSearching"
                >
                    <LoadingState :loading="true" />
                </li>
                <li
                    v-for="(airport, aIndex) in airports"
                    :key="`airport-${aIndex}`"
                    @click="selectAirport(airport)"
                    class="w-full cursor-pointer p-2 hover:bg-slate-100 dark:hover:bg-slate-700 dark:bg-slate-700 transition-colors gap-2 rounded-md flex items-center justify-between"
                    :class="{ '!bg-slate-200 dark:bg-slate-700': airport.active }"
                    role="button"
                >
                    <span class="text-sm">
                        <span class="font-bold uppercase">
                            {{ airport.code }}
                        </span>
                        -
                        {{ airport.name }}
                    </span>
                </li>
                <li
                    class="w-full p-4 gap-2 rounded-md flex"
                    v-if="!isSearching && airports?.length === 0"
                >
                    No results found, try adjusting your search.
                </li>
                <li
                    class="cursor-pointer p-2 bg-slate-50 dark:bg-slate-900/40 hover:bg-slate-100 dark:hover:bg-slate-700 dark:bg-slate-700 transition-colors gap-2 rounded-md flex items-center justify-between absolute left-1 right-1 bottom-1 text-blue-400"
                    role="button"
                    v-if="airportsQuery.length > 2"
                    @click="useQuery()"
                >
                    Use "{{ airportsQuery }}"
                </li>
            </ul>
        </div>
    </div>
</template>
<script setup>
import { ref, computed, watch } from 'vue';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';
import Icon from '@/components/ui/icon/icon.vue';
import { Apii } from '@/services/api';

const ApiService = new Apii();

const props = defineProps({
    label: {
        type: String,
        required: true,
        default: 'Airport',
    },
    placeholder: {
        type: String,
        default: 'Search',
    },
    currentAirport: {
        type: String,
        default: null,
    },
    hasError: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['airportChanged']);

const airportsQuery = ref('');
const queryStartLength = ref(0);
const airports = ref([]);
const activeIndex = ref(-1);
const scrollAmount = ref(42);
const inputFocused = ref(false);
const isSearching = ref(false);
const airportChosen = ref(null);
const airportChosenDisplay = ref(null);

const fetchAirportDetails = (code) => {
    ApiService.apiCall(`/airports/search?search=${code}`, 'get')
        .then((response) => {
            const airport = response.data.find(
                (a) => a.code.toUpperCase() === code.toUpperCase()
            );
            if (airport) {
                airportChosenDisplay.value = `${airport.name}, ${airport.city}, ${airport.country}`;
            } else {
                airportChosenDisplay.value = code;
            }
        })
        .catch(() => {
            airportChosenDisplay.value = code;
        });
};

watch(
    () => props.currentAirport,
    (newValue) => {
        if (newValue) {
            airportChosen.value = newValue;
            // If it's just a code, fetch the full airport details
            if (newValue && newValue.length <= 4) {
                fetchAirportDetails(newValue);
            } else {
                airportChosenDisplay.value = newValue;
            }
        }
    },
    { immediate: true }
);

const scrollContainer = computed(() => {
    return document.getElementById('airport-dropdown');
});

const clearSearch = () => {
    airportsQuery.value = null;
    airports.value = [];
    isSearching.value = false;
};

const searchAirports = () => {
    isSearching.value = true;
    if (airportsQuery.value && airportsQuery.value.trim() !== '') {
        ApiService.apiCall(
            '/airports/search?search=' + airportsQuery.value.trim(),
            'get'
        )
            .then((response) => {
                airports.value = response.data;
                isSearching.value = false;
            })
            .catch((error) => {
                isSearching.value = false;
            });
    } else {
        airports.value = [];
        isSearching.value = false;
    }
};

const selectAirport = (airport) => {
    airportChosen.value = airport.code.toUpperCase();
    airportChosenDisplay.value = `${airport.name}, ${airport.city}, ${airport.country}`;
    emit('airportChanged', {
        code: airport.code.toUpperCase(),
        name: airport.name,
    });
    clearSearch();
};

const enter = () => {
    // Check if we have valid airports and a valid activeIndex
    if (
        airports.value &&
        airports.value.length > 0 &&
        activeIndex.value >= 0 &&
        activeIndex.value < airports.value.length
    ) {
        const airport = airports.value[activeIndex.value];
        airportChosen.value = airport.code.toUpperCase();
        emit('airportChanged', airport.code.toUpperCase());
    } else if (airportsQuery.value && airportsQuery.value.trim() !== '') {
        useQuery();
        return;
    }
    clearSearch();
};

const useQuery = () => {
    if (airportsQuery.value && airportsQuery.value.trim() !== '') {
        airportChosen.value = airportsQuery.value.trim().toUpperCase();
        emit('airportChanged', airportsQuery.value.trim());
        clearSearch();
    }
};

const updateFocus = (val) => {
    inputFocused.value = val;
};

const resetActiveItem = () => {
    airports.value.forEach((item) => {
        item.active = false;
    });
};

const reset = () => {
    airportChosen.value = null;
    airportChosenDisplay.value = null;
    clearSearch();
    emit('airportChanged', null);
};

const up = () => {
    if (activeIndex.value >= 0) {
        if (activeIndex.value > 0) {
            activeIndex.value--;
        }

        resetActiveItem();
        airports.value[activeIndex.value].active = true;

        if (airports.value.length - activeIndex.value > 3) {
            scrollContainer.value.scrollTop =
                scrollContainer.value.scrollTop - scrollAmount.value;
        }
    }
};

const down = () => {
    if (
        activeIndex.value > -2 &&
        activeIndex.value + 1 < airports.value.length
    ) {
        activeIndex.value++;
        resetActiveItem();
        airports.value[activeIndex.value].active = true;

        if (activeIndex.value > 3) {
            scrollContainer.value.scrollTop =
                scrollAmount.value + scrollContainer.value.scrollTop;
        }
    }
};
</script>
